import { LocalData } from "./LocalData";
import { PropertyCollectionItemData } from "../Property/Collections/PropertyCollectionItemData";
import { RoutineData } from "../Routines/RoutineData";
import { DeviationData } from "../Deviation/DeviationData";
import { SubtaskData } from "../Deviation/SubtaskData";
import { PropertyData } from "../Property/PropertyData";
import { DocumentReference, doc, getFirestore } from "firebase/firestore";
import { UnitData } from "../Property/Units/UnitData";
import { V2CollectionItemData } from "./V2CollectionItemData";

export enum RelativeDataType {
  DEVIATION = 0,
  ROUTINE = 1,
  COLLECTION_ITEM = 2,
  EXTERNAL = 3,
  SUBTASK = 4,
  TENANT = 5,
  TENANT_FILE = 6,
}
export interface RelativeDataExternal {
  apiClientID: string;
  type: string;
  id: string;
}
export class RelativeData {
  id: string;
  type?: RelativeDataType;
  reference?: RoutineData | DeviationData | PropertyCollectionItemData | V2CollectionItemData | SubtaskData | UnitData;
  external?: RelativeDataExternal;
  name: string = "";

  constructor(id: string, data: any) {
    this.id = id;
    this.type =
      typeof data.type === "number"
        ? data.type === 0
          ? RelativeDataType.DEVIATION
          : data.type === 1
          ? RelativeDataType.ROUTINE
          : data.type === 2
          ? RelativeDataType.COLLECTION_ITEM
          : data.type === 3
          ? RelativeDataType.EXTERNAL
          : data.type === 4
          ? RelativeDataType.SUBTASK
          : data.type === 5
          ? RelativeDataType.TENANT
          : data.type === 6
          ? RelativeDataType.TENANT_FILE
          : undefined
        : undefined;
    let ref: DocumentReference | undefined = undefined;
    if (typeof data.reference === "object" && data.reference.parent) {
      ref = data.reference as DocumentReference;
    } else if (typeof data.reference === "string") {
      const db = getFirestore();
      ref = doc(db, data.reference);
    }

    if (ref) {
      if (this.type === RelativeDataType.DEVIATION) {
        const deviationID = ref.id;
        const propertyID = ref.parent.parent?.id;
        if (propertyID) {
          this.reference = LocalData.properties.addData(propertyID).deviations.addData(deviationID);
        }
      } else if (this.type === RelativeDataType.ROUTINE) {
        this.reference = LocalData.routines.addData(ref.id);
      } else if (this.type === RelativeDataType.COLLECTION_ITEM) {
        const itemID = ref.id;
        const collectionID = ref.parent.parent?.id;
        const propertyID = ref.parent.parent?.parent.parent?.id;
        if (collectionID && propertyID) {
          this.reference = LocalData.properties.addData(propertyID).collections.addData(collectionID).items.addData(itemID);
        } else if (ref.parent.path === LocalData.collectionItems.getPath()) {
          this.reference = LocalData.collectionItems.addData(itemID);
        }
      } else if (this.type === RelativeDataType.SUBTASK) {
        const subtaskID = ref.id;
        const deviationID = ref.parent.parent?.id;
        const propertyID = ref.parent.parent?.parent.parent?.id;
        if (propertyID && deviationID) {
          this.reference = LocalData.properties.addData(propertyID).deviations.addData(deviationID).subtasks.addData(subtaskID);
        }
      } else if (this.type === RelativeDataType.TENANT) {
        const propertyID = ref.parent.parent?.id;

        if (propertyID) {
          this.reference = LocalData.properties.addData(propertyID).getUnitWithId(ref.id);
        }
      }
    }
    if (typeof data.external === "object") {
      this.external = {
        apiClientID: typeof data.external.apiClientID === "string" ? data.external.apiClientID : "",
        type: typeof data.external.type === "string" ? data.external.type : "",
        id: typeof data.external.id === "string" ? data.external.id : "",
      };
    }
    this.name = typeof data.name === "string" ? data.name : "";
  }

  getProperty() {
    if (this.reference instanceof DeviationData) {
      return this.reference.parent as PropertyData;
    } else if (this.reference instanceof PropertyCollectionItemData) {
      return this.reference.parent?.parent as PropertyData;
    } else if (this.reference instanceof SubtaskData) {
      return this.reference.deviation.parent as PropertyData;
    } else if (this.reference instanceof UnitData) {
      return this.reference.property;
    } else if (this.reference instanceof V2CollectionItemData) {
      return this.reference.properties.length > 0 ? this.reference.properties[0] : undefined;
    }
    return undefined;
  }

  /**
   * Get the raw data of this object, to store in Firebase
   */
  getRawData() {
    const data: { [key: string]: any } = {};
    if (this.type !== undefined) data.type = this.type;
    if (this.reference) data.reference = doc(getFirestore(), this.reference.getCompletePath());
    if (this.external) data.external = this.external;
    if (this.name) data.name = this.name;
    return data;
  }
}

export class RelativeHelper {
  static canViewRelative(relative: RelativeData) {
    if (relative.reference instanceof RoutineData && relative.reference.canViewRoutine()) {
      return true;
    } else if (relative.reference instanceof DeviationData && relative.reference.canViewDeviation()) {
      return true;
    } else if (relative.reference instanceof PropertyCollectionItemData && relative.reference.hasReadAccess()) {
      return true;
    } else if (relative.reference instanceof V2CollectionItemData && relative.reference.hasReadAccess()) {
      return true;
    } else if (relative.reference instanceof SubtaskData && relative.reference.deviation.canViewDeviation()) {
      return true;
    }
    return false;
  }

  static getRelativeLink(relative: RelativeData): string | undefined {
    if (relative.reference instanceof RoutineData) {
      return "/routines/" + relative.reference.id;
    } else if (relative.reference instanceof DeviationData && relative.reference.parent instanceof PropertyData) {
      return `/properties/${relative.reference.parent.id}/tasks/${relative.reference.id}`;
    } else if (relative.reference instanceof PropertyCollectionItemData) {
      return `/properties/${relative.reference.parent?.parent?.id}/collections/${relative.reference.parent?.id}/items/${relative.reference.id}`;
    } else if (relative.reference instanceof V2CollectionItemData) {
      return `/collectionItems/${relative.reference.id}`;
    } else if (relative.reference instanceof SubtaskData && relative.reference.deviation.parent instanceof PropertyData) {
      return `/properties/${relative.reference.deviation.parent.id}/tasks/${relative.reference.deviation.id}`;
    } else if (relative.reference instanceof UnitData && relative.reference.property) {
      return `/properties/${relative.reference.property.id}/units/${relative.reference.id}`;
    }
    return "";
  }
}

